import {
  useUserStore,
  userStoreSelector,
} from '@repo/editor/src/stores/userStore';
import { withEventScope } from '@repo/event-processor';
import { fireToast } from '@repo/ui/components/Toast';
import { capitalizeFirstLetter } from '@repo/ui/utils';
import IconChevronDown from '@repo/ui-icons/IconChevronDown';
import { Badge } from '@repo/ui-kit/components/core/Badge';
import { Popover } from '@repo/ui-kit/components/core/Popover';

import { FlexColumn } from 'styles/layouts';

import { SelectedAccount, TeamsContainer, TeamSelectorWrapper } from './styles';
import TeamSelectorRow from './TeamSelectorRow';
import { useTeamsForTeamSelector } from './useTeamsForTeamSelector';

const TeamSelector: React.FC = () => {
  const user = useUserStore(userStoreSelector.user);
  const swapMainUser = useUserStore(userStoreSelector.swapMainUser);
  const loginTeam = useUserStore(userStoreSelector.loginTeam);

  const allAccounts = useTeamsForTeamSelector();
  /* The team selector should be shown only if team account(s) exist for the logged-in user */
  if (!allAccounts || !user) {
    return null;
  }

  // Function called when team selector options are clicked (account switching)
  const handleChangeProfile = async (slug?: string): Promise<void> => {
    if (!slug || user.slug === slug) {
      return;
    }

    const teamAccount = allAccounts
      ? allAccounts.find(
          (account) => account.slug === slug && !account.isMainAccount
        )
      : null;

    if (teamAccount?.slug) {
      const response = await loginTeam({ slug: teamAccount.slug });
      if ('error' in response) {
        fireToast({ label: 'Unfortunately, an error occured', error: true });
        return;
      }
    } else {
      await swapMainUser();
      return;
    }
  };

  return (
    <TeamSelectorWrapper>
      <Popover.Root>
        <Popover.Trigger>
          <SelectedAccount>
            <TeamSelectorRow
              image={user.profileImage || ''}
              name={user.name || ''}
            />
            <IconChevronDown color="var(--color-text-secondary)" size={16} />
          </SelectedAccount>
        </Popover.Trigger>
        <Popover.Content>
          <FlexColumn>
            {allAccounts.map((account) => {
              if (user.slug !== account.slug && account.active) {
                return (
                  <TeamsContainer
                    key={account.slug}
                    onClick={() => handleChangeProfile(account.slug || '')}
                  >
                    <TeamSelectorRow
                      image={account.profileImage || ''}
                      name={account.name || ''}
                    />
                    <Badge
                      size="s"
                      text={capitalizeFirstLetter(
                        (account.plan || 'FREE').toLowerCase()
                      )}
                      variant="soft-info"
                    />
                  </TeamsContainer>
                );
              }
              return;
            })}
          </FlexColumn>
        </Popover.Content>
      </Popover.Root>
    </TeamSelectorWrapper>
  );
};

export default withEventScope(TeamSelector, { module: 'teamSelector' });
